import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime } from '../../services/format';
import { Alert, Badge, Button, Card, KeyValue, PageTitle, Tabs, statusTone } from '../../components/ui';

export function MailDetail() {
  const { id } = useParams();
  const mailId = Number(id);
  const { t, i18n } = useTranslation();
  const { data: mail, error, reload } = useAsync(() => api.mailDetail(mailId), [mailId]);
  const [view, setView] = useState<'html' | 'text'>('html');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);

  const retry = async () => {
    if (!mail) return;
    setBusy(true);
    setFail(null);
    try {
      await api.retryMail(mail.id);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert>{error}</Alert>;
  if (!mail) return <p className="text-faint">{t('common.loading')}</p>;
  return (
    <div>
      <PageTitle
        title={
          <>
            {mail.subject} <Badge tone={statusTone(mail.status)}>{t(`mail.status.${mail.status}`)}</Badge>
          </>
        }
        subtitle={<span className="font-mono">#{mail.id} · {mail.template} · {mail.to_email}</span>}
        actions={
          mail.status !== 'sent' && (
            <Button variant="primary" busy={busy} onClick={() => void retry()} data-testid="mail-retry">
              {t('mail.retry')}
            </Button>
          )
        }
      />
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
        <div className="flex flex-col gap-4">
          <Card title={t('mail.fields')}>
            <KeyValue
              rows={[
                [t('mail.to'), <span key="to" className="font-mono text-[11.5px]">{mail.to_name ? `${mail.to_name} <${mail.to_email}>` : mail.to_email}</span>],
                [t('users.locale'), mail.locale],
                [t('mail.attempts'), mail.attempts],
                [t('mail.scheduledAt'), dateTime(mail.scheduled_at, i18n.language)],
                [t('mail.sentAt'), dateTime(mail.sent_at, i18n.language)],
                [t('mail.providerId'), mail.provider_message_id ?? '—'],
                [t('mail.attachment'), mail.attachment_name ?? '—'],
                [t('mail.lastError'), mail.last_error ? <span key="e" className="font-mono text-[11.5px] text-danger">{mail.last_error}</span> : '—'],
                [t('orgs.createdAt'), dateTime(mail.created_at, i18n.language)],
              ]}
            />
          </Card>
          <Card title={t('mail.params')}>
            <pre className="overflow-auto rounded-md border border-line-soft bg-code p-2 font-mono text-[11px] leading-relaxed text-ink">{JSON.stringify(mail.params, null, 2)}</pre>
          </Card>
        </div>
        <Card title={t('mail.preview')}>
          <Tabs tabs={[{ id: 'html', label: 'HTML' }, { id: 'text', label: t('mail.text') }]} active={view} onChange={setView} />
          {view === 'html' ? (
            <iframe title={t('mail.preview')} srcDoc={mail.html} sandbox="" className="mt-3 h-[560px] w-full rounded-md border border-line-soft bg-white" data-testid="mail-html" />
          ) : (
            <pre className="mt-3 whitespace-pre-wrap rounded-md border border-line-soft bg-code p-3 font-mono text-[11.5px] leading-relaxed text-ink">{mail.text}</pre>
          )}
        </Card>
      </div>
    </div>
  );
}
